import React from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import styles from './Notifications.module.scss';
import { Notification } from '../types/notification.types';
import { fetchUserNotifications, markNotificationAsRead } from '../api/notificationsApi';
import { mapAPINotificationsToNotifications } from '../utils/notificationMappers';

interface NotificationsProps {
	isOpen: boolean;
	onClose: () => void;
}

const Notifications: React.FC<NotificationsProps> = ({ isOpen, onClose }) => {
	const history = useHistory();
	const queryClient = useQueryClient();

	// Fetch notifications from API
	const { data: apiNotifications = [], isLoading } = useQuery(
		'userNotifications',
		fetchUserNotifications,
		{
			refetchInterval: 30000, // Refetch every 30 seconds
			staleTime: 10000,
		}
	);

	const notifications = mapAPINotificationsToNotifications(apiNotifications);
	const unreadCount = notifications.filter((n) => !n.isRead).length;

	// Mutation for marking notification as read
	const markAsReadMutation = useMutation(markNotificationAsRead, {
		onSuccess: () => {
			queryClient.invalidateQueries('userNotifications');
		},
	});

	const markAsRead = (id: string) => {
		markAsReadMutation.mutate(id);
	};

	const markAllAsRead = async () => {
		const unreadNotifications = notifications.filter((n) => !n.isRead);
		for (const notification of unreadNotifications) {
			await markNotificationAsRead(notification.id);
		}
		queryClient.invalidateQueries('userNotifications');
	};

	const handleNotificationClick = (notification: Notification) => {
		if (!notification.isRead) {
			markAsRead(notification.id);
		}
		if (notification.actionUrl) {
			history.push(notification.actionUrl);
			onClose();
		}
	};

	const formatTimestamp = (timestamp: Date): string => {
		const now = new Date();
		const diff = now.getTime() - timestamp.getTime();
		const minutes = Math.floor(diff / (1000 * 60));
		const hours = Math.floor(diff / (1000 * 60 * 60));
		const days = Math.floor(diff / (1000 * 60 * 60 * 24));

		if (minutes < 1) return 'Just now';
		if (minutes < 60) return `${minutes}m ago`;
		if (hours < 24) return `${hours}h ago`;
		if (days === 1) return 'Yesterday';
		if (days < 7) return `${days}d ago`;
		return timestamp.toLocaleDateString();
	};

	if (!isOpen) return null;

	return (
		<>
			<div className={styles.notificationsOverlay} onClick={onClose} />
			<div className={styles.notificationsContainer}>
				<div className={styles.notificationsHeader}>
					<div className={styles.headerLeft}>
						<h3>Notifications</h3>
						{unreadCount > 0 && <span className={styles.unreadBadge}>{unreadCount}</span>}
					</div>
					{unreadCount > 0 && (
						<button className={styles.markAllButton} onClick={markAllAsRead}>
							Mark all as read
						</button>
					)}
				</div>

				<div className={styles.notificationsList}>
					{isLoading ? (
						<div className={styles.emptyState}>
							<p>Loading notifications...</p>
						</div>
					) : notifications.length === 0 ? (
						<div className={styles.emptyState}>
							<p>No notifications</p>
						</div>
					) : (
						notifications.map((notification) => (
							<div
								key={notification.id}
								className={`${styles.notificationItem} ${
									!notification.isRead ? styles.unread : ''
								} ${styles[notification.type]}`}
								onClick={() => handleNotificationClick(notification)}
							>
								<div className={styles.notificationContent}>
									<div className={styles.notificationHeader}>
										<h4>{notification.title}</h4>
										<span className={styles.timestamp}>
											{formatTimestamp(notification.timestamp)}
										</span>
									</div>
									<p className={styles.notificationMessage}>{notification.message}</p>
								</div>
								{!notification.isRead && (
									<button
										className={styles.markReadButton}
										onClick={(e) => {
											e.stopPropagation();
											markAsRead(notification.id);
										}}
									>
										Mark as read
									</button>
								)}
							</div>
						))
					)}
				</div>
			</div>
		</>
	);
};

export default Notifications;
